import React, { useEffect, useState } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { PiWarningCircleLight } from "react-icons/pi";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { serverInstance } from "../../service/axios";
import useRedirectIfAuthenticated from "../../hooks/useRedirectIfAuthenticated";
import { handleApiError } from "../../utils/ErrorMessageUtils";
import "./sign.scss";

const SignIn = ({ data }) => {
  const navigate = useNavigate();
  useRedirectIfAuthenticated();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: "",
    mobile_ext: "+976",
    mobile_number: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [method, setMethod] = useState("mobile");
  const [loginMode, setLoginMode] = useState("password"); // "password" or "otp"
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  // Read tab value from query parameters on component mount
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "email" || tabParam === "mobile") {
      setMethod(tabParam);
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // Convert email to lowercase before sending
    const apiData = {
      ...formData,
      email: formData.email.toLowerCase()
    };

    serverInstance
      .post("/school/signin", apiData)
      .then((response) => {
        localStorage.setItem("lingoPortalToken", response.data.token);
        navigate("/school/client");
      })
      .catch((error) => {
        console.error("SignIn Error:", error);
        setErrorMsg(handleApiError(data, error?.response?.data?.message));
        setLoading(false);
      });
  };

  const validateOTPInput = () => {
    if (method === "mobile") {
      if (!formData.mobile_number.trim()) {
        setErrorMsg(data.mobile_number_required || "Утасны дугаар оруулна уу");
        return false;
      }
      
      // Validate mobile number format (8 digits)
      const mobilePattern = /^[0-9]{8}$/;
      if (!mobilePattern.test(formData.mobile_number)) {
        setErrorMsg(data.invalid_mobile || "Утасны дугаар буруу байна");
        return false;
      }
    } else if (method === "email") {
      if (!formData.email.trim()) {
        setErrorMsg(data.email_required || "Мэйл хаяг оруулна уу");
        return false;
      }
      
      // Basic email validation
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.email)) {
        setErrorMsg(data.invalid_email || "Мэйл хаяг буруу байна");
        return false;
      }
    }
    return true;
  };

  const sendOTP = async () => {
    setOtpLoading(true);
    setErrorMsg("");

    // Validate input before sending OTP
    if (!validateOTPInput()) {
      setOtpLoading(false);
      return;
    }


    try {
      const endpoint = method === "email" ? "/email/send" : "/sms/send";
      const requestData = method === "email" 
        ? {
            email: formData.email.toLowerCase(),
            lang: data.language ? "en" : "mn"
          }
        : {
            mobile_ext: formData.mobile_ext,
            mobile_number: formData.mobile_number,
            lang: data.language ? "en" : "mn"
          };

      const response = await serverInstance.post(endpoint, requestData);

      if (response.status === 200) {
        setOtpSent(true);
        setErrorMsg("");
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message;
      setErrorMsg(handleApiError(data, errorMessage));
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otpCode.trim()) {
      setErrorMsg(data.enterOTP || "OTP код оруулна уу");
      return;
    }

    setOtpLoading(true);
    setErrorMsg("");

    try {
      const requestData = method === "email"
        ? {
            email: formData.email.toLowerCase(),
            code: otpCode,
            lang: data.language ? "en" : "mn"
          }
        : {
            mobile_ext: formData.mobile_ext,
            mobile_number: formData.mobile_number,
            code: otpCode,
            lang: data.language ? "en" : "mn"
          };

      const response = await serverInstance.post("/school/signin", requestData);

      if (response.status === 200) {
        localStorage.setItem("lingoPortalToken", response.data.token);
        navigate("/school/client");
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message;
      setErrorMsg(handleApiError(data, errorMessage));
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="sign-container">
      <div className="sign-container-card">
        {/* {loading ? <Dimmer /> : null} */}
        <h2>{data.title}</h2>

        {/* Tab Header */}
        <div className="tab-header">
          <button
            type="button"
            onClick={() => setMethod("mobile")}
            className={`tab-button ${method === "mobile" ? "active" : ""}`}
          >
            {data.smsTab}
          </button>
          <button
            type="button"
            onClick={() => setMethod("email")}
            className={`tab-button ${method === "email" ? "active" : ""}`}
          >
            {data.emailTab}
          </button>
        </div>

        <form className="sign-form" onSubmit={handleSubmit}>
          {errorMsg ? (
            <div className="form-div error-msg">
              <PiWarningCircleLight />
              &nbsp;{errorMsg}
            </div>
          ) : null}
          
          {method === "mobile" ?
          <div className="form-div">
            <label className="sign-label">{data.mobile_number}</label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <select
                name="mobile_ext"
                value={formData.mobile_ext}
                onChange={handleChange}
                required
                className="sign-input"
                style={{ flex: '0 0 120px' }}
              >
                {data.countryCodes?.map((country, index) => (
                  <option key={index} value={country.dial_code}>
                    {country.dial_code} {country.code}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                name="mobile_number"
                pattern="[0-9]{8}"
                value={formData.mobile_number}
                onChange={handleChange}
                required
                className="sign-input"
                style={{ flex: '1' }}
              />
            </div>
          </div>
          :
          <div className="form-div">
            <label className="sign-label">{data.email}</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="sign-input"
            />
          </div>
          }
          
          {/* Only show password field for password login mode */}
          {loginMode === "password" && (
            <div className="form-div">
              <label className="sign-label">{data.password}</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="sign-input"
              />
            </div>
          )}

          <div className="form-div">
            <div className="flexrow">
              <div></div>
              {method === "email" && (
                <Link to={"/forgot-password"} className="sign-text forgot">
                  {data.forgotPassword}
                </Link>
              )}
            </div>
            
            {loginMode === "password" ? (
              <button type="submit" className="sign-button" disabled={loading}>
                {loading ? (
                  <AiOutlineLoading3Quarters size={20} className="loading-spinner" />
                ) : (
                  data.signin
                )}
              </button>
            ) : (
              <div>
                {!otpSent ? (
                  <button 
                    type="button" 
                    className="sign-button" 
                    onClick={sendOTP}
                    disabled={otpLoading}
                  >
                    {otpLoading ? (
                      <AiOutlineLoading3Quarters size={20} className="loading-spinner" />
                    ) : (
                      data.sendOTP
                    )}
                  </button>
                ) : (
                  <div>
                    <div className="form-div">
                      <label className="sign-label">{data.enterOTP}</label>
                      <input
                        type="text"
                        placeholder="6 оронтой код оруулна уу"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value)}
                        className="sign-input"
                        maxLength={6}
                      />
                    </div>
                    <button 
                      type="button" 
                      className="sign-button" 
                      onClick={verifyOTP}
                      disabled={otpLoading}
                    >
                      {otpLoading ? (
                        <AiOutlineLoading3Quarters size={20} className="loading-spinner" />
                      ) : (
                        data.verifyOTP
                      )}
                    </button>
                    <button 
                      type="button" 
                      className="sign-button3" 
                      onClick={sendOTP}
                      disabled={otpLoading}
                      style={{ marginTop: "10px" }}
                    >
                      {data.resendOTP}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flexrow">
            <div
              style={{
                border: "1px solid gray",
                height: "1px",
                width: "20%",
              }}
            />
            <p className="gray-text">{data.textOne}</p>
            <div
              style={{
                border: "1px solid gray",
                height: "1px",
                width: "20%",
              }}
            />
          </div>
          
          {/* OTP Login Option */}
          <div className="form-div" style={{ marginTop: "20px", marginBottom: "20px" }}>
            <button 
              type="button" 
              className="sign-button2" 
              onClick={() => {
                setLoginMode(loginMode === "password" ? "otp" : "password");
                setOtpSent(false);
                setOtpCode("");
                setErrorMsg("");
              }}
            >
              {loginMode === "password" ? data.loginWithOTP : data.backToPassword}
            </button>
          </div>
          
          <div className="form-div signup-link">
            <Link to={"/signup"} className="sign-text">
              <button className="sign-button2" type="submit">
                {data.textTwo}
              </button>
            </Link>
          </div>
          <div className="form-div signup-link"></div>
        </form>
      </div>
    </div>
  );
};

export default SignIn;
